import React, { useEffect, useMemo, useState } from "react";
import useSWR, { mutate as globalMutate } from "swr";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchScenarios, fetchScenario, fetchMeta } from "@/lib/api";
import Header from "@/components/app/Header";
import ScenarioBar from "@/components/app/ScenarioBar";
import GlobalControls from "@/components/app/GlobalControls";
import OverviewTab from "@/components/app/OverviewTab";
import LineItemsTab from "@/components/app/LineItemsTab";
import RateCompareTab from "@/components/app/RateCompareTab";
import CompareTab from "@/components/app/CompareTab";
import { LayoutDashboard, ListTree, GitCompareArrows, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import useSWRImmutable from "swr/immutable";
import axios from "axios";

export default function Dashboard() {
  const { boqId } = useParams();
  const [activeId, setActiveId] = useState(null);
  const { data: boq } = useSWRImmutable(boqId ? `/boqs/${boqId}` : null, (u) => axios.get(u).then(r => r.data));
  const { data: meta } = useSWR("meta", fetchMeta);
  const { data: scenarios, mutate: mutateScenarios } = useSWR("scenarios", fetchScenarios);

  useEffect(() => {
    if (!activeId && scenarios && scenarios.length > 0) {
      const baseline = scenarios.find(s => s.is_baseline) || scenarios[0];
      setActiveId(baseline.id);
    }
  }, [scenarios, activeId]);

  const { data: activeScenario, mutate: mutateActive } = useSWR(
    activeId ? ["scenario", activeId] : null,
    () => fetchScenario(activeId)
  );

  const baselineId = useMemo(() => scenarios?.find(s => s.is_baseline)?.id, [scenarios]);

  const refreshAll = () => {
    mutateActive();
    mutateScenarios();
    globalMutate("scenarios");
  };

  if (!scenarios) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {boq?.vendor && (
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 pt-3 pb-1 flex items-center gap-2 text-xs">
          <Link to="/projects" className="text-muted-foreground hover:text-primary flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Projects
          </Link>
          {boq.vendor.project_id && (
            <>
              <span className="text-muted-foreground">/</span>
              <Link to={`/projects/${boq.vendor.project_id}`} className="text-muted-foreground hover:text-primary">Project</Link>
            </>
          )}
          <span className="text-muted-foreground">/</span>
          <Link to={`/vendors/${boq.vendor.id}`} className="text-muted-foreground hover:text-primary">{boq.vendor.name}</Link>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{boq.quotation_no}</span>
        </div>
      )}
      <Header meta={meta} />

      <main className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <ScenarioBar
          scenarios={scenarios}
          activeId={activeId}
          onSelect={setActiveId}
          onChange={refreshAll}
        />

        {activeScenario && (
          <GlobalControls
            scenario={activeScenario.scenario}
            onChange={refreshAll}
          />
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-white shadow-sm border border-border" data-testid="main-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="gap-2">
              <LayoutDashboard className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="items" data-testid="tab-items" className="gap-2">
              <ListTree className="h-4 w-4" /> Line Items
            </TabsTrigger>
            <TabsTrigger value="rate-compare" data-testid="tab-rate-compare" className="gap-2">
              <Sparkles className="h-4 w-4" /> Rate Compare
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare" className="gap-2">
              <GitCompareArrows className="h-4 w-4" /> Scenarios
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {activeScenario ? (
              <OverviewTab data={activeScenario} baselineId={baselineId} />
            ) : (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            )}
          </TabsContent>

          <TabsContent value="items" className="mt-6">
            {activeScenario ? (
              <LineItemsTab data={activeScenario} onChange={refreshAll} />
            ) : (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            )}
          </TabsContent>

          <TabsContent value="rate-compare" className="mt-6">
            {activeScenario ? (
              <RateCompareTab data={activeScenario} onChange={refreshAll} />
            ) : (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            )}
          </TabsContent>

          <TabsContent value="compare" className="mt-6">
            <CompareTab scenarios={scenarios} activeId={activeId} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
